/**
 * One child's page, for their parent.
 *
 * The dashboard promises "Emma has 2 missing assignments"; this is where that
 * promise lands. Missing work leads, because it is the thing a parent can do
 * something about tonight — grades and attendance are the standing record
 * behind it.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  AlertTriangle, CalendarCheck, CheckCircle2, ChevronDown, ChevronRight, GraduationCap,
} from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { DataTableFacetedFilter } from '~/components/ui/data-table-faceted-filter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import type { AppLayoutContext } from '~/types';
import { useStudentAcademicSummary } from '~/components/StudentAcademics/useStudentAcademicSummary';

import type { ResultOf } from 'gql.tada';
import { StudentAcademicSummaryQuery } from '~/components/StudentAcademics/useStudentAcademicSummary';

type SummaryClassGrade = NonNullable<
  NonNullable<ResultOf<typeof StudentAcademicSummaryQuery>>['studentAcademicSummary']
>['classGrades'][number];

/**
 * A subject appears once per term across the year, and the terms that have not
 * started carry no percentage. Keep the one with a real standing.
 */
function gradedSubjects(classGrades: readonly SummaryClassGrade[]): SummaryClassGrade[] {
  const bySubject = new Map<string, SummaryClassGrade>();
  for (const grade of classGrades) {
    if (typeof grade.result?.percentage !== 'number') continue;
    if (!bySubject.has(grade.transcriptName)) bySubject.set(grade.transcriptName, grade);
  }
  return [...bySubject.values()];
}

const STATUS_LABELS: Record<string, string> = {
  PRESENT: 'Present',
  TARDY: 'Tardy',
  ABSENT: 'Absent',
  EXCUSED: 'Excused',
};

const STATUS_OPTIONS = [
  { value: 'PRESENT', label: 'Present' },
  { value: 'TARDY', label: 'Tardy' },
  { value: 'ABSENT', label: 'Absent' },
  { value: 'EXCUSED', label: 'Excused' },
];

type AttendanceDay = {
  date: unknown;
  status: string;
  blockTitle: string;
  scheduleBlockId: string;
};

function AttendanceDayList({ days }: { days: readonly AttendanceDay[] }) {
  const [classFilter, setClassFilter] = useState<Set<string>>(() => new Set());
  const [statusFilter, setStatusFilter] = useState<Set<string>>(() => new Set());
  const classOptions = useMemo(
    () => [...new Set(days.map((day) => day.blockTitle))]
      .sort((a, b) => a.localeCompare(b))
      .map((title) => ({ value: title, label: title })),
    [days],
  );
  const visible = useMemo(
    () => days.filter((day) => (
      (classFilter.size === 0 || classFilter.has(day.blockTitle))
      && (statusFilter.size === 0 || statusFilter.has(day.status))
    )),
    [days, classFilter, statusFilter],
  );

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>
              <DataTableFacetedFilter
                title="Class"
                options={classOptions}
                selectedValues={classFilter}
                onSelectedValuesChange={setClassFilter}
              />
            </TableHead>
            <TableHead className="text-right">
              <div className="flex justify-end">
                <DataTableFacetedFilter
                  title="Attendance"
                  options={STATUS_OPTIONS}
                  selectedValues={statusFilter}
                  onSelectedValuesChange={setStatusFilter}
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground">
                No recorded days match these filters.
              </TableCell>
            </TableRow>
          ) : visible.map((day) => (
            <TableRow key={`${String(day.date)}-${day.scheduleBlockId}`}>
              <TableCell>{formatAttendanceDay(day.date)}</TableCell>
              <TableCell className="text-muted-foreground">{day.blockTitle}</TableCell>
              <TableCell className="text-right font-medium">
                {STATUS_LABELS[day.status] ?? day.status}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function formatAttendanceDay(value: unknown): string {
  const iso = String(value).slice(0, 10);
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

const STATE_LABELS: Record<string, string> = {
  MISSING: 'Not handed in',
  EXCUSED: 'Excused',
  UNGRADED: 'Not graded yet',
};

/** One subject, openable to show the work the grade is made of. */
function SubjectRow({ grade }: { grade: SummaryClassGrade }) {
  const [open, setOpen] = useState(false);
  const percentage = grade.result?.percentage as number;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border-b last:border-0">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-muted/40"
          aria-label={`${open ? 'Hide' : 'Show'} assignments for ${grade.transcriptName}`}
        >
          <span className="flex items-center gap-2">
            {open
              ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
              : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            {grade.transcriptName}
          </span>
          {/*
            The subject total stays in plain text. Coloring both the total and
            every score under it turned the page into a wall of alarm, and the
            scores are where a parent actually looks.
          */}
          <span className="text-sm font-medium">
            {percentage.toFixed(1)}%
            {grade.result?.letterGrade ? ` · ${grade.result.letterGrade}` : ''}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-3">
          {grade.assignments.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">No posted work in this subject yet.</p>
          ) : (
            <ul className="divide-y rounded-md border bg-background">
              {grade.assignments.map((assignment) => {
                const scored = assignment.state === 'GRADED' || assignment.state === 'MISSING';
                return (
                  <li
                    key={assignment.assignmentId}
                    className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium">{assignment.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {assignment.dueOn ? `Due ${String(assignment.dueOn)}` : 'No due date'}
                        {assignment.state !== 'GRADED' && ` · ${STATE_LABELS[assignment.state] ?? assignment.state}`}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 font-medium ${
                        scored && assignment.gradeColor ? 'grade-color' : 'text-muted-foreground'
                      }`}
                      style={scored && assignment.gradeColor
                        ? ({ '--grade-color': assignment.gradeColor } as CSSProperties)
                        : undefined}
                    >
                      {assignment.state === 'EXCUSED' || assignment.state === 'UNGRADED'
                        ? '—'
                        : `${assignment.pointsEarned ?? 0} / ${assignment.pointsPossible}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function formatDue(value: unknown): string {
  return value ? String(value) : 'No due date';
}

export default function ParentStudentPage() {
  const { personId } = useParams();
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const selectedYear = roleContext?.selectedSchoolYear;

  const { data, loading } = useStudentAcademicSummary(school?.id, selectedYear?.id, personId);
  const summary = data?.studentAcademicSummary;

  const grades = useMemo(() => gradedSubjects(summary?.classGrades ?? []), [summary]);

  if (loading) return <div className="p-6 text-muted-foreground">Loading…</div>;

  if (!summary) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold">Student not found</h1>
        <p className="mt-2 text-muted-foreground">
          This student isn't part of your family for {selectedYear?.name ?? 'this year'}.
        </p>
      </div>
    );
  }

  const missing = summary.missingAssignments ?? [];
  const attendance = summary.attendance;
  const fullName = `${summary.person.firstName} ${summary.person.lastName}`;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <GraduationCap className="h-7 w-7 text-muted-foreground" />
        <div>
          <h1 className="text-3xl font-bold">{fullName}</h1>
          <p className="text-muted-foreground">{selectedYear?.name}</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            {missing.length > 0
              ? <AlertTriangle className="h-5 w-5 text-red-600" />
              : <CheckCircle2 className="h-5 w-5 text-green-600" />}
            Missing work
            {missing.length > 0 && <Badge variant="destructive">{missing.length}</Badge>}
          </CardTitle>
          <CardDescription>
            Work a teacher has marked as not handed in. Work that simply hasn't been graded yet
            isn't counted here.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {missing.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              Nothing is missing. Every assignment has been handed in or excused.
            </div>
          ) : (
            <ul className="divide-y rounded-md border">
              {missing.map((assignment) => (
                <li key={assignment.assignmentId} className="flex items-center justify-between gap-4 px-4 py-3">
                  <div className="min-w-0">
                    <p className="font-medium">{assignment.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {assignment.className} · due {formatDue(assignment.dueOn)}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm text-muted-foreground">
                    {assignment.pointsPossible} pts
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Grades</CardTitle>
          <CardDescription>
            Where {summary.person.firstName} stands right now. Open a subject to see the work behind
            the grade, most recent first.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {grades.length === 0 ? (
            <p className="text-sm text-muted-foreground">No graded work yet this year.</p>
          ) : (
            <div className="rounded-md border">
              {grades.map((grade) => <SubjectRow key={grade.transcriptName} grade={grade} />)}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <CalendarCheck className="h-5 w-5 text-muted-foreground" />
            Attendance
          </CardTitle>
          <CardDescription>Recorded days this year. Blank days are not counted as absences.</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {attendance.recordedCount === 0 ? (
            <p className="text-sm text-muted-foreground">No attendance recorded yet this year.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                {([
                  ['Present', attendance.presentCount],
                  ['Tardy', attendance.tardyCount],
                  ['Absent', attendance.absentCount],
                  ['Excused', attendance.excusedCount],
                ] as const).map(([label, value]) => (
                  <div key={label} className="rounded-lg border p-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                    <p className="text-2xl font-semibold">{value}</p>
                  </div>
                ))}
                <div className="rounded-lg border p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Rate</p>
                  <p className="text-2xl font-semibold">
                    {attendance.attendanceRate == null ? '—' : `${attendance.attendanceRate}%`}
                  </p>
                </div>
              </div>
              {(attendance.days ?? []).length > 0 && (
                <AttendanceDayList days={attendance.days ?? []} />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
